/* global Word */

import type { LayoutConfig, TemplateProfile } from "../../../templates/types";
import type { ExhibitInsertionStrategy } from "../types";
import { logger } from "../../../core/logging";
import { TableBuilder } from "../../builders/TableBuilder";
import { setupHeaderCell, setupGraphCell, setupSourceCell } from "../cellSetup";

/**
 * Inserts a single full-width exhibit table (3 rows × 1 column).
 * Row 0 = header, Row 1 = graph placeholder, Row 2 = source.
 */
export class FullStrategy implements ExhibitInsertionStrategy {
  readonly layoutType = "full" as const;

  async execute(
    
    context: Word.RequestContext,
    anchorRange: Word.Range,
    config: LayoutConfig,
    profile: TemplateProfile,
    startingExhibitNumber: number
  ): Promise<Word.Table> {
    logger.debug('[FullStrategy] execute called', { colWidth: config.colWidth });

    // ── 1. Insert table shell ────────────────────────────────────────────────
    // No collection properties are available on a freshly inserted table.
    const table = anchorRange.insertTable(3, 1, Word.InsertLocation.after);
    TableBuilder.clearAllBorders(table);
    TableBuilder.lockWidth(table, config.colWidth);

    // ── 2. FIRST load+sync — get row references for height mutation ──────────
    table.rows.load("items");
    await context.sync();

    // Use a clearly named variable. Do NOT reuse this after the next sync.
    const rowsForHeight = table.rows.items;
    rowsForHeight[0].setHeight(config.row1Height, Word.RowHeightRule.atLeast);
    rowsForHeight[1].setHeight(config.row2Height, Word.RowHeightRule.atLeast);
    rowsForHeight[2].setHeight(config.row3Height, Word.RowHeightRule.atLeast);

    // ── 3. SECOND load+sync — height mutation invalidated all row refs ────────
    // This is mandatory. The previous rowsForHeight array is now stale.
    table.rows.load("items");
    await context.sync();

    // Fresh references after the second sync.
    const rows = table.rows.items;

    // ── 4. Load cells for all three rows in a single batch sync ─────────────
    rows[0].cells.load("items");
    rows[1].cells.load("items");
    rows[2].cells.load("items");
    await context.sync();

    // ── 5. Setup each cell — refs are fresh and cells are loaded ────────────
    await setupHeaderCell(context, rows[0].cells.items[0], startingExhibitNumber, profile);
    await setupGraphCell(context, rows[1].cells.items[0], { height: config.row2Height, width: config.colWidth });
    await setupSourceCell(context, rows[2].cells.items[0], profile);

    return table;
  }
}